import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, Loader2, Printer, ShieldCheck, ShieldOff } from 'lucide-react';
import { dateFr } from '../utils/dates';
import {
  activer2fa,
  desactiver2fa,
  fetchEtat2fa,
  preparer2fa,
  regenererCodesSecours2fa,
} from '../services/api';

/**
 * La connexion en deux étapes, vue du membre.
 *
 * Le parcours tient en trois écrans successifs : le mot de passe, le QR à
 * scanner (avec sa transcription pour qui lit cette page sur le téléphone
 * même), puis un premier code qui prouve que l'application est bien réglée.
 * Les codes de secours s'affichent alors une seule fois — d'où le bouton
 * d'impression, à ne pas manquer.
 */
export default function DoubleFacteur() {
  const queryClient = useQueryClient();
  const etat = useQuery({ queryKey: ['2fa'], queryFn: fetchEtat2fa });

  const [etape, setEtape] = useState<'repos' | 'motDePasse' | 'scan' | 'arret'>('repos');
  const [motDePasse, setMotDePasse] = useState('');
  const [code, setCode] = useState('');
  const [enrolement, setEnrolement] = useState<{ qr: string; secret: string } | null>(null);
  const [codesSecours, setCodesSecours] = useState<string[] | null>(null);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [copie, setCopie] = useState(false);

  const rearmer = () => {
    setEtape('repos');
    setMotDePasse('');
    setCode('');
    setEnrolement(null);
    queryClient.invalidateQueries({ queryKey: ['2fa'] });
  };

  const preparer = useMutation({
    mutationFn: () => preparer2fa(motDePasse),
    onSuccess: (d: any) => {
      setEnrolement({ qr: d.qr, secret: d.secret });
      setNotice(null);
      setEtape('scan');
    },
    onError: (e: Error) => setNotice({ type: 'error', message: e.message }),
  });

  const activer = useMutation({
    mutationFn: () => activer2fa(code),
    onSuccess: (d: any) => {
      setCodesSecours(d.codesSecours);
      setNotice({ type: 'success', message: d.message });
      rearmer();
    },
    onError: (e: Error) => setNotice({ type: 'error', message: e.message }),
  });

  const desactiver = useMutation({
    mutationFn: () => desactiver2fa(motDePasse, code),
    onSuccess: (d: any) => {
      setCodesSecours(null);
      setNotice({ type: 'success', message: d.message });
      rearmer();
    },
    onError: (e: Error) => setNotice({ type: 'error', message: e.message }),
  });

  const refaireCodes = useMutation({
    mutationFn: () => regenererCodesSecours2fa(motDePasse),
    onSuccess: (d: any) => {
      setCodesSecours(d.codesSecours);
      setNotice({ type: 'success', message: d.message });
      rearmer();
    },
    onError: (e: Error) => setNotice({ type: 'error', message: e.message }),
  });

  if (etat.isPending) {
    return (
      <div className="card lg:p-6 flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const actif = etat.data?.actif === true;
  const restants = etat.data?.codesSecoursRestants ?? 0;

  const imprimerCodes = () => {
    if (!codesSecours) return;
    const fenetre = window.open('', '_blank', 'width=600,height=700');
    if (!fenetre) return;
    fenetre.document.write(`
      <html><head><title>Codes de secours</title>
      <style>
        body { font-family: system-ui, sans-serif; padding: 32px; color: #111; }
        h1 { font-size: 18px; }
        p { font-size: 13px; color: #444; }
        ul { list-style: none; padding: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        li { font-family: ui-monospace, monospace; font-size: 16px; letter-spacing: 1px;
             border: 1px solid #ddd; border-radius: 8px; padding: 8px 12px; }
      </style></head><body>
      <h1>Codes de secours — connexion en deux étapes</h1>
      <p>Chacun ne sert qu'une fois, à la place du code de votre application.
         Rangez cette feuille ailleurs que dans votre téléphone.</p>
      <ul>${codesSecours.map((c) => `<li>${c}</li>`).join('')}</ul>
      </body></html>`);
    fenetre.document.close();
    fenetre.print();
  };

  return (
    <div className="card lg:p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100">
            {actif ? (
              <ShieldCheck className="w-5 h-5 text-green-600" />
            ) : (
              <ShieldOff className="w-5 h-5 text-gray-400" />
            )}
            Connexion en deux étapes
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {actif ? (
              <>
                Active depuis le{' '}
                {etat.data?.activeLe
                  ? dateFr(etat.data.activeLe)
                  : '—'}{' '}
                — un code vous sera demandé à la connexion, sauf sur les appareils que vous avez
                choisi de reconnaître ({etat.data?.joursConfiance ?? 30} jours).
              </>
            ) : (
              <>
                Un code à six chiffres, en plus du mot de passe : même volé, votre mot de passe ne
                suffit plus à entrer. Il vous faut une application d'authentification sur votre
                téléphone (Google Authenticator, Authy, Bitwarden, ou le trousseau iPhone).
              </>
            )}
          </p>
        </div>
        {actif && restants > 0 && (
          <span className="text-xs rounded-full px-2.5 py-1 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 whitespace-nowrap">
            {restants} code{restants > 1 ? 's' : ''} de secours
          </span>
        )}
      </div>

      {notice && (
        <p
          className={`text-sm rounded-xl px-3 py-2 ${
            notice.type === 'success'
              ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300'
              : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
          }`}
        >
          {notice.message}
        </p>
      )}

      {/* Les codes de secours : montrés une seule fois, à l'activation ou
          quand on en refait une série. */}
      {codesSecours && (
        <div className="rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10 p-4 space-y-3">
          <p className="text-sm text-amber-900 dark:text-amber-200 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <strong>Notez-les maintenant</strong> : ils ne seront plus jamais affichés. Chacun
              remplace une fois le code de l'application — c'est ce qui vous sauve si vous perdez
              votre téléphone.
            </span>
          </p>
          <ul className="grid grid-cols-2 gap-2">
            {codesSecours.map((c) => (
              <li
                key={c}
                className="font-mono text-sm tracking-wider rounded-lg bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-800 px-3 py-2 text-center"
              >
                {c}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2">
            <button onClick={imprimerCodes} className="btn-outline inline-flex items-center gap-2 text-sm px-3 py-1.5">
              <Printer className="w-4 h-4" /> Imprimer
            </button>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(codesSecours.join('\n'));
                setCopie(true);
                setTimeout(() => setCopie(false), 2000);
              }}
              className="btn-outline inline-flex items-center gap-2 text-sm px-3 py-1.5"
            >
              {copie ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              {copie ? 'Copiés' : 'Copier'}
            </button>
            <button
              onClick={() => setCodesSecours(null)}
              className="text-sm text-gray-500 dark:text-gray-400 hover:underline px-2"
            >
              J'ai noté ces codes
            </button>
          </div>
        </div>
      )}

      {/* Activation : mot de passe, puis QR, puis premier code. */}
      {!actif && etape === 'repos' && (
        <button onClick={() => { setNotice(null); setEtape('motDePasse'); }} className="btn-primary inline-flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> Activer la connexion en deux étapes
        </button>
      )}

      {!actif && etape === 'motDePasse' && (
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Confirmez votre mot de passe
            </span>
            <input
              type="password"
              autoComplete="current-password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              className="input-field mt-1 max-w-sm"
            />
          </label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => preparer.mutate()}
              disabled={preparer.isPending || motDePasse === ''}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {preparer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Continuer
            </button>
            <button onClick={rearmer} className="text-sm text-gray-500 dark:text-gray-400 hover:underline">
              Annuler
            </button>
          </div>
        </div>
      )}

      {!actif && etape === 'scan' && enrolement && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-5 items-start">
            <img
              src={enrolement.qr}
              alt="QR code à scanner avec votre application d'authentification"
              className="w-44 h-44 rounded-xl border border-gray-200 dark:border-gray-700 bg-white"
            />
            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 max-w-sm">
              <p>
                <strong className="text-gray-900 dark:text-gray-100">1.</strong> Scannez ce code
                avec votre application d'authentification.
              </p>
              <p>
                <strong className="text-gray-900 dark:text-gray-100">2.</strong> Saisissez
                ci-dessous le code à six chiffres qu'elle affiche.
              </p>
              <p className="pt-1">
                Vous lisez cette page sur le téléphone qui porte l'application ? Saisissez plutôt
                cette clé à la main :
              </p>
              <code className="block font-mono text-xs bg-gray-100 dark:bg-gray-800 rounded-lg px-3 py-2 break-all">
                {enrolement.secret}
              </code>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="input-field !w-36 tracking-widest"
            />
            <button
              onClick={() => activer.mutate()}
              disabled={activer.isPending || code.length < 6}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {activer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Activer
            </button>
            <button onClick={rearmer} className="text-sm text-gray-500 dark:text-gray-400 hover:underline">
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Compte protégé : refaire des codes, ou couper la protection. */}
      {actif && etape === 'repos' && (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => { setNotice(null); setEtape('motDePasse'); }} className="btn-outline text-sm px-3 py-1.5">
            Refaire des codes de secours
          </button>
          <button
            onClick={() => { setNotice(null); setEtape('arret'); }}
            className="text-sm text-red-600 dark:text-red-400 hover:underline px-2"
          >
            Désactiver
          </button>
        </div>
      )}

      {actif && etape === 'motDePasse' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Les codes de secours actuels cesseront de valoir.
          </p>
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Votre mot de passe"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            className="input-field max-w-sm"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => refaireCodes.mutate()}
              disabled={refaireCodes.isPending || motDePasse === ''}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {refaireCodes.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Générer de nouveaux codes
            </button>
            <button onClick={rearmer} className="text-sm text-gray-500 dark:text-gray-400 hover:underline">
              Annuler
            </button>
          </div>
        </div>
      )}

      {actif && etape === 'arret' && (
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50/60 dark:bg-red-900/10 p-4 space-y-3">
          <p className="text-sm text-red-800 dark:text-red-300 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Votre compte ne sera plus protégé que par son mot de passe. Mot de passe et code
              sont tous deux demandés pour couper la protection.
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Votre mot de passe"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              className="input-field !w-56"
            />
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="input-field !w-32 tracking-widest"
            />
            <button
              onClick={() => desactiver.mutate()}
              disabled={desactiver.isPending || motDePasse === '' || code === ''}
              className="inline-flex items-center gap-2 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 px-4 py-2.5 text-sm font-semibold text-white"
            >
              {desactiver.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Désactiver
            </button>
            <button onClick={rearmer} className="text-sm text-gray-500 dark:text-gray-400 hover:underline px-2">
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
